/**
 * Replays queued offline mutations against the live API.
 *
 * Trigger points:
 *   - On app load (only if `navigator.onLine`).
 *   - On `window`'s `online` event.
 *   - On user-driven "Sync now" button.
 *
 * Items are replayed in insertion order. A single failure for one item does
 * not block the rest — the failing item is left in the queue with an
 * incremented `attempts` counter and the latest error. A permanent (4xx that
 * isn't 401/408/429) failure could be auto-discarded, but for v1 we keep the
 * record so the user can see what's stuck.
 */
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { listQueue, removeQueued, updateQueued, setMeta } from "@/lib/offlineDb";
import { addUpload, updateByQueueId, updateUpload } from "@/lib/uploadStatus";

// Per-item retry spacing so a big queued batch isn't re-sent every 20s.
const BASE_DELAY = 20_000;
const MAX_DELAY = 10 * 60_000;
const JSA_WAIT = 5 * 60_000;
const backoffMs = (attempts) => Math.min(MAX_DELAY, BASE_DELAY * 2 ** Math.max(0, attempts - 1));
import { speak } from "@/lib/speak";
import { uploadVideoChunked } from "@/lib/videoUpload";

const isMedia = (it) => it.kind === "upload" || it.kind === "video";

let _running = false;
let _pendingForce = false;

async function replayOne(item) {
  // Use the shared axios instance so the `withCredentials: true` flag
  // ensures the `f2_token` cookie rides along — same auth path as a
  // live request, no manual header juggling.
  if (item.kind === "upload") {
    const fd = new FormData();
    Object.entries(item.formFields || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, v);
    });
    fd.append("file", item.file, item.filename || "photo.jpg");
    return api.post(`/projects/${item.projectId}/upload`, fd);
  }
  if (item.kind === "video") {
    return uploadVideoChunked({
      projectId: item.projectId,
      file: new File([item.file], item.filename || "video.mp4", { type: item.contentType || "video/mp4" }),
      fields: item.formFields || {},
      onProgress: (pct) => updateByQueueId(item.id, { status: "uploading", message: `Uploading video ${pct}%` }),
    });
  }
  if (item.kind === "tailgate") {
    return api.post(`/projects/${item.projectId}/tailgate`, item.body);
  }
  if (item.kind === "jsa") {
    return api.post(`/projects/${item.projectId}/jsa`, item.body);
  }
  if (item.kind === "patch") {
    // For queued patches the URL is absolute (it was captured at enqueue
    // time). Strip the API_BASE prefix so the shared `api` instance can
    // resolve it relative to its baseURL.
    const rel = (item.url || "").startsWith(API_BASE)
      ? item.url.slice(API_BASE.length)
      : item.url;
    return api.patch(rel, item.body || {});
  }
  if (item.kind === "post") {
    const rel = (item.url || "").startsWith(API_BASE)
      ? item.url.slice(API_BASE.length)
      : item.url;
    return api.post(rel, item.body || {});
  }
  throw new Error(`Unknown queue kind: ${item.kind}`);
}

/**
 * Drain the queue. Returns `{ replayed, failed, remaining }`.
 * `silent=true` suppresses toasts (used for the auto-fire on app boot).
 */
export async function syncQueue({ silent = false, force = !silent } = {}) {
  if (_running) {
    // Remember a forced request (e.g. JSA just saved) and re-run when the current drain ends.
    if (force) _pendingForce = true;
    return { replayed: 0, failed: 0, remaining: 0, skipped: true };
  }
  if (!navigator.onLine) return { replayed: 0, failed: 0, remaining: 0, offline: true };
  _running = true;
  let replayed = 0;
  let failed = 0;
  let photosDone = 0;
  let photosDropped = 0;
  let touched = false;
  try {
    const now = Date.now();
    // Background drains only replay items whose backoff has elapsed; manual/online drains replay all.
    const items = (await listQueue()).filter((it) => force || !it.nextAttemptAt || it.nextAttemptAt <= now);
    if (items.length === 0) return { replayed: 0, failed: 0, remaining: 0 };
    touched = true;
    if (!silent) {
      toast.info(`Syncing ${items.length} pending change${items.length === 1 ? "" : "s"}…`);
    }
    for (const it of items) {
      try {
        await replayOne(it);
        await removeQueued(it.id);
        replayed += 1;
        if (isMedia(it)) {
          photosDone += 1;
          updateByQueueId(it.id, { status: "done", message: "" });
        }
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        // JSA gate: keep the photo queued until the JSA is completed.
        if (status === 403 && String(detail).startsWith("Complete the JSA")) {
          failed += 1;
          await updateQueued(it.id, {
            attempts: (it.attempts || 0) + 1,
            lastError: detail,
            nextAttemptAt: Date.now() + JSA_WAIT,
          });
          updateByQueueId(it.id, { status: "waiting", message: "Waiting for the JSA to be completed" });
          continue;
        }
        // 4xx (except 429 rate-limited) means the request will never
        // succeed no matter how many times we retry — drop it so we
        // stop toasting the same error on every online event.
        // Common cases: 400 malformed payload, 403 role changed since
        // queue, 404 project was deleted while offline, 409 N/A gate.
        const permanent =
          typeof status === "number" && status >= 400 && status < 500 &&
          ![401, 408, 429].includes(status);
        // Signed out / session expired: keep everything and stop until re-login.
        if (status === 401) {
          failed += 1;
          break;
        }
        if (permanent) {
          await removeQueued(it.id);
          failed += 1;
          if (isMedia(it)) {
            photosDropped += 1;
            const why = detail || `Server rejected (${status})`;
            const patch = { status: "failed", message: why };
            // After a reload the row is gone — re-add it so the loss stays visible.
            if (!updateByQueueId(it.id, patch)) updateUpload(addUpload(it.filename || "photo", it.label || ""), patch);
            // Always warn, even during silent background drains: this photo is gone from the device.
            const what = it.label ? `the "${it.label}" photo` : "a waiting photo";
            toast.error(`Couldn't upload ${what} (${it.filename || "photo"}) and it was removed: ${why}. Please retake it.`, { duration: 15000 });
          } else if (!silent) {
            toast.error(
              `Dropped queued ${it.kind || "change"} — ` +
              (err?.response?.data?.detail || `server rejected (${status})`)
            );
          }
          continue;
        }
        failed += 1;
        const attempts = (it.attempts || 0) + 1;
        await updateQueued(it.id, {
          attempts,
          nextAttemptAt: Date.now() + backoffMs(attempts),
          lastError:
            err?.response?.data?.detail ||
            err?.message ||
            "Unknown error",
        });
        // Network blip mid-drain — stop and let the next trigger pick up.
        if (!err?.response) break;
      }
    }
    await setMeta("lastSyncAt", Date.now());
    if (photosDone || photosDropped) {
      const n = (k) => `${k} waiting photo${k === 1 ? "" : "s"}`;
      speak([
        photosDone ? `${n(photosDone)} finished uploading` : "",
        photosDropped ? `${n(photosDropped)} could not be uploaded and must be retaken` : "",
      ].filter(Boolean).join(". "));
    }
    if (!silent) {
      if (failed === 0 && replayed > 0) {
        toast.success(`Synced ${replayed} change${replayed === 1 ? "" : "s"}`);
      } else if (failed > 0) {
        toast.error(`${replayed} synced, ${failed} still pending`);
      }
    }
    return { replayed, failed, remaining: failed };
  } finally {
    _running = false;
    if (_pendingForce) {
      _pendingForce = false;
      setTimeout(() => { syncQueue({ silent: true, force: true }); }, 0);
    }
    if (touched) window.dispatchEvent(new CustomEvent("f2:queue-changed", { detail: { replayed } }));
  }
}

/** Mounted once at app start. Listens for online + initial drain. */
export function installSyncListeners() {
  if (window.__f2_sync_installed) return;
  window.__f2_sync_installed = true;
  // Initial drain on boot (fire-and-forget).
  setTimeout(() => { syncQueue({ silent: true }); }, 1500);
  window.addEventListener("online", () => { syncQueue({ silent: false }); });
  // Storage-busy queued photos while still online: keep draining in the background.
  setInterval(() => { if (navigator.onLine) syncQueue({ silent: true }); }, 20000);
}
